// src/services/azureResourcePlanningService.js
// Azure Table Storage Service für Ressourcenplanung

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "resourcePlanning",
  sasToken: "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D"
};

const createRowKey = (projectNumber, employeeUuid, weekNumber, year) => {
  return `${projectNumber}_${employeeUuid}_${weekNumber}_${year}`;
};

// Helper function to fetch all records with pagination
const fetchAllRecords = async (baseUrl) => {
  let allRecords = [];
  let nextPartitionKey = null;
  let nextRowKey = null;

  do {
    let url = baseUrl;
    if (nextPartitionKey && nextRowKey) {
      const separator = baseUrl.includes('$filter') ? '&' : '&';
      url += `${separator}NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
      url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
    }

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Table Error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    allRecords = allRecords.concat(data.value || []);

    nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
    nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
  } while (nextPartitionKey && nextRowKey);

  return allRecords;
};

export const loadResourcePlanning = async (code) => {
  try {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=Code eq '${code}'`;
    const records = await fetchAllRecords(baseUrl);

    const resourceData = {};
    records.forEach(item => {
      const year = item.Year || new Date().getFullYear();
      const weekKey = `KW${item.WeekNumber}_${year}`;
      if (!resourceData[weekKey]) {
        resourceData[weekKey] = {};
      }
      resourceData[weekKey][item.EmployeeUuid] = item.Hours || 0;
    });

    return resourceData;
  } catch (error) {
    console.error("Load Resource Planning Error:", error);
    return {};
  }
};

export const saveResourcePlanning = async (projectNumber, resourceData) => {
  try {
    await deleteProjectResourcePlanning(projectNumber);

    const savePromises = [];
    
    Object.entries(resourceData).forEach(([weekKey, employees]) => {
      const [kwPart, yearStr] = weekKey.split('_');
      const weekNumber = parseInt(kwPart.replace('KW', ''));
      const year = yearStr ? parseInt(yearStr) : new Date().getFullYear();
      
      Object.entries(employees).forEach(([employeeUuid, hours]) => {
        if (hours > 0) {
          savePromises.push(
            saveResourceEntry(projectNumber, employeeUuid, weekNumber, year, hours)
          );
        }
      });
    });

    await Promise.all(savePromises);
    return true;
  } catch (error) {
    console.error("Save Resource Planning Error:", error);
    throw error;
  }
};

const saveResourceEntry = async (projectNumber, employeeUuid, weekNumber, year, hours) => {
  const rowKey = createRowKey(projectNumber, employeeUuid, weekNumber, year);
  const now = new Date().toISOString();

  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='resource',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const entity = {
    PartitionKey: "resource",
    RowKey: rowKey,
    ProjectNumber: projectNumber,
    EmployeeUuid: employeeUuid,
    WeekNumber: weekNumber,
    Year: year,
    Hours: hours,
    CreatedAt: now,
    UpdatedAt: now
  };

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json;odata=nometadata"
    },
    body: JSON.stringify(entity)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Azure Save Error: ${response.status} - ${errorText}`);
  }

  return true;
};

const deleteProjectResourcePlanning = async (projectNumber) => {
  try {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=ProjectNumber eq '${projectNumber}'`;
    const records = await fetchAllRecords(baseUrl);

    const deletePromises = records.map(entry =>
      deleteResourceEntry(entry.RowKey)
    );

    await Promise.all(deletePromises);
  } catch (error) {
    console.error("Delete Project Resource Planning Error:", error);
  }
};

const deleteResourceEntry = async (rowKey) => {
  const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='resource',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      "If-Match": "*"
    }
  });

  if (!response.ok && response.status !== 404) {
    const errorText = await response.text();
    console.error(`Delete Error: ${response.status} - ${errorText}`);
  }
};

export const loadAllResourcePlanning = async () => {
  try {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;
    const records = await fetchAllRecords(baseUrl);

    const projectData = {};
    records.forEach(item => {
      const projectNumber = item.ProjectNumber;
      const year = item.Year || new Date().getFullYear();
      const weekKey = `KW${item.WeekNumber}_${year}`;

      if (!projectData[projectNumber]) {
        projectData[projectNumber] = {};
      }
      if (!projectData[projectNumber][weekKey]) {
        projectData[projectNumber][weekKey] = {};
      }

      projectData[projectNumber][weekKey][item.EmployeeUuid] = item.Hours;
    });

    return projectData;
  } catch (error) {
    console.error("Load All Resource Planning Error:", error);
    return {};
  }
};

export const savePersonResourcePlanning = async (employeeUuid, resourceData) => {
  try {
    await deleteEmployeeResourcePlanning(employeeUuid);

    const savePromises = [];
    
    Object.entries(resourceData).forEach(([weekKey, projects]) => {
      const [kwPart, yearStr] = weekKey.split('_');
      const weekNumber = parseInt(kwPart.replace('KW', ''));
      const year = yearStr ? parseInt(yearStr) : new Date().getFullYear();
      
      Object.entries(projects).forEach(([projectRef, hours]) => {
        if (hours > 0) {
          savePromises.push(
            saveResourceEntry(projectRef, employeeUuid, weekNumber, year, hours)
          );
        }
      });
    });

    await Promise.all(savePromises);
    return true;
  } catch (error) {
    console.error("Save Person Resource Planning Error:", error);
    throw error;
  }
};

const deleteEmployeeResourcePlanning = async (employeeUuid) => {
  try {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=EmployeeUuid eq '${employeeUuid}'`;
    const records = await fetchAllRecords(baseUrl);

    const deletePromises = records.map(entry =>
      deleteResourceEntry(entry.RowKey)
    );

    await Promise.all(deletePromises);
  } catch (error) {
    console.error("Delete Employee Resource Planning Error:", error);
  }
};

export const loadEmployeeResourcePlanning = async (employeeUuid) => {
  try {
    const baseUrl = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&$filter=EmployeeUuid eq '${employeeUuid}'`;
    const records = await fetchAllRecords(baseUrl);

    const weeklyData = {};
    records.forEach(item => {
      const year = item.Year || new Date().getFullYear();
      const weekKey = `KW${item.WeekNumber}_${year}`;

      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {};
      }

      weeklyData[weekKey][item.ProjectNumber] = item.Hours;
    });

    return weeklyData;
  } catch (error) {
    console.error("Load Employee Resource Planning Error:", error);
    return {};
  }
};